import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const GSTVerificationModal = ({ 
  isOpen, 
  onClose, 
  onDataReceived, 
  initialGSTIN = ''
}) => {
  const [gstin, setGstin] = useState(initialGSTIN);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setGstin(initialGSTIN);
      setCaptchaCode('');
      setError('');
      setVerificationResult(null);
      fetchCaptcha();
    } else {
      // Reset all state when modal closes
      setGstin('');
      setCaptchaCode('');
      setCaptchaImage('');
      setSessionId('');
      setError('');
      setVerificationResult(null);
    }
  }, [isOpen, initialGSTIN]);

  // Fetch captcha from GST API
  const fetchCaptcha = async () => {
    setIsLoadingCaptcha(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/gst/captcha`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.sessionId && data.image) {
        setCaptchaImage(data.image);
        setSessionId(data.sessionId);
        console.log('Captcha loaded successfully, session expires in:', data.expiresIn, 'seconds');
      } else {
        setError(data.error || 'Failed to fetch captcha from GST portal');
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error: Unable to connect to GST verification service.');
      } else {
        setError('Failed to load captcha. Please check your internet connection and try again.');
      }
      console.error('Captcha fetch error:', err);
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  // Validate GSTIN format
  const validateGSTIN = (gstinValue) => {
    if (!gstinValue || gstinValue.length !== 15) {
      return 'GSTIN must be 15 characters long';
    }
    
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstRegex.test(gstinValue)) {
      return 'Invalid GSTIN format';
    }
    
    return null;
  };

  // Handle GSTIN input change
  const handleGSTINChange = (e) => {
    const value = e.target.value.toUpperCase().trim();
    setGstin(value);
    setError('');
    setVerificationResult(null);
  };

  // Verify GST details
  const verifyGST = async () => {
    // Validate inputs
    const gstinError = validateGSTIN(gstin);
    if (gstinError) {
      setError(gstinError);
      return;
    }

    if (!captchaCode.trim()) {
      setError('Please enter the captcha code');
      return;
    }

    if (!sessionId) {
      setError('Session expired. Please refresh captcha');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/gst/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: sessionId,
          GSTIN: gstin,
          captcha: captchaCode
        })
      });

      const data = await response.json();

      if (response.ok && data.gstin) {
        console.log('GST Verification Success - Full Response:', data);
        console.log('GST Verification Success - Extracted Data:', data.extractedData);
        setVerificationResult(data);
        setError('');
        
        // Call the callback with the verified data and close modal
        if (onDataReceived) {
          console.log('Calling onDataReceived with data:', {
            gstData: data,
            gstin: gstin,
            useData: true
          });
          onDataReceived({
            gstData: data,
            gstin: gstin,
            useData: true // This tells the parent component to populate the form
          });
          
          // Close modal after sending data to parent
          setTimeout(() => {
            onClose();
          }, 500);
        }
      } else {
        // Handle specific error cases
        const errorMessage = data.error || 'Failed to verify GSTIN';
        
        if (errorMessage.includes('expired session') || errorMessage.includes('Invalid session')) {
          setError('Session expired. Loading new captcha...');
        } else if (errorMessage.includes('Too many attempts')) {
          setError('Too many verification attempts. Loading new captcha...');
        } else if (errorMessage.includes('Invalid GSTIN or captcha')) {
          setError('Invalid GSTIN or captcha code. Please check and try again.');
        } else {
          setError(errorMessage);
        }
        
        // Refresh captcha on error
        setTimeout(() => fetchCaptcha(), 1000);
        setCaptchaCode('');
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error: Unable to connect to verification service.');
      } else {
        setError('Verification failed. Please try again.');
      }
      console.error('GST verification error:', err);
      // Refresh captcha on error
      setTimeout(() => fetchCaptcha(), 1000);
      setCaptchaCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    verifyGST();
  };

  // Use verified data and close modal
  const useVerifiedData = () => {
    if (verificationResult && onDataReceived) {
      onDataReceived({
        gstData: verificationResult,
        gstin: gstin,
        useData: true
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm">
      <div className={`rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden border-2 ${
        darkMode
          ? 'bg-gradient-to-br from-slate-900 to-slate-800 border-blue-500/30'
          : 'bg-white border-purple-200/50'
      }`}>
        {/* Premium Header */}
        <div className={`px-4 sm:px-6 py-4 sm:py-5 border-b flex justify-between items-center ${
          darkMode
            ? 'bg-gradient-to-r from-slate-900/95 via-blue-900/90 to-purple-900/95 border-slate-700/50'
            : 'bg-gradient-to-r from-blue-600 to-cyan-600 border-purple-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 sm:p-2.5 rounded-xl shadow-lg ${
              darkMode
                ? 'bg-gradient-to-br from-blue-600 to-purple-600'
                : 'bg-gradient-to-br from-white/20 to-white/10'
            }`}>
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className={`text-lg sm:text-xl font-black ${
                darkMode
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
                  : 'text-white'
              }`}>
                GST Verification
              </h2>
              <p className={`text-xs sm:text-sm mt-1 ${
                darkMode ? 'text-blue-300' : 'text-blue-100'
              }`}>
                Verify GSTIN with Government Portal
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isVerifying}
            className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-110 hover:rotate-90 active:scale-95 ${
              darkMode
                ? 'text-red-400 hover:bg-red-900/30 disabled:opacity-50'
                : 'text-white hover:bg-white/20 disabled:opacity-50'
            }`}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* GSTIN Input */}
            <div>
              <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>
                📄 GSTIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={gstin}
                onChange={handleGSTINChange}
                placeholder="Enter 15-digit GSTIN (e.g., 22AAAAA0000A1Z5)"
                maxLength="15"
                className={`w-full px-4 py-3 text-sm sm:text-base rounded-xl border-2 font-mono font-bold uppercase transition-all focus:outline-none focus:ring-4 shadow-md ${
                  darkMode
                    ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                    : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                }`}
                disabled={isVerifying}
                required
              />
              <p className={`text-xs mt-2 ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}>
                Format: 15 characters (State Code + PAN + Entity No + Z + Check Digit)
              </p>
            </div>

            {/* Captcha Section */}
            <div className="space-y-4">
              <label className={`block text-sm font-black uppercase tracking-wider ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>
                🔒 Captcha <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start">
                {/* Captcha Image */}
                <div className="flex-shrink-0 w-full sm:w-auto">
                  {isLoadingCaptcha ? (
                    <div className={`w-full sm:w-48 h-24 rounded-xl border-2 flex items-center justify-center ${
                      darkMode 
                        ? 'bg-slate-800 border-slate-600' 
                        : 'bg-purple-50 border-purple-300'
                    }`}>
                      <Loader2 className={`w-6 h-6 animate-spin ${
                        darkMode ? 'text-blue-400' : 'text-purple-600'
                      }`} />
                    </div>
                  ) : captchaImage ? (
                    <div className={`w-full sm:w-48 h-24 rounded-xl border-2 overflow-hidden ${
                      darkMode ? 'border-slate-600' : 'border-purple-300'
                    }`}>
                      <img
                        src={captchaImage}
                        alt="Captcha"
                        className="w-full h-full object-contain bg-white"
                        style={{
                          imageRendering: 'crisp-edges',
                          filter: 'contrast(1.2) brightness(1.1)'
                        }}
                      />
                    </div>
                  ) : (
                    <div className={`w-full sm:w-48 h-24 rounded-xl border-2 flex items-center justify-center ${
                      darkMode 
                        ? 'bg-slate-800 border-slate-600' 
                        : 'bg-purple-50 border-purple-300'
                    }`}>
                      <span className={`text-xs ${
                        darkMode ? 'text-slate-400' : 'text-purple-500'
                      }`}>
                        No captcha
                      </span>
                    </div>
                  )}
                </div>

                {/* Refresh Button */}
                <button
                  type="button"
                  onClick={fetchCaptcha}
                  disabled={isLoadingCaptcha || isVerifying}
                  className={`px-4 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md flex items-center gap-2 min-h-[44px] touch-manipulation ${
                    isLoadingCaptcha || isVerifying
                      ? 'opacity-50 cursor-not-allowed'
                      : darkMode
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                  }`}
                  title="Refresh Captcha"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingCaptcha ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>

              {/* Captcha Input */}
              <div>
                <input
                  type="text"
                  value={captchaCode}
                  onChange={(e) => setCaptchaCode(e.target.value)}
                  placeholder="Enter captcha code"
                  maxLength={6}
                  className={`w-full px-4 py-3 text-sm sm:text-base rounded-xl border-2 font-mono font-bold uppercase transition-all focus:outline-none focus:ring-4 shadow-md ${
                    darkMode
                      ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                      : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                  }`}
                  disabled={isVerifying || !captchaImage}
                  required
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className={`flex items-center gap-2 p-3 rounded-xl border-2 ${
                darkMode
                  ? 'bg-red-900/30 border-red-700 text-red-300'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Verification Result */}
            {verificationResult && (
              <div className={`border-2 rounded-xl p-4 ${
                darkMode
                  ? 'border-green-600 bg-green-900/30'
                  : 'border-green-200 bg-green-50'
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className={`font-black uppercase tracking-wider ${
                    darkMode ? 'text-green-400' : 'text-green-800'
                  }`}>
                    ✅ Verification Successful
                  </span>
                </div>
                
                <div className="space-y-3 text-sm max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🏢 Legal Name:
                      </span>
                      <p className={`font-medium break-words ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {verificationResult.lgnm || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🏪 Trade Name:
                      </span>
                      <p className={`font-medium break-words ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {verificationResult.tradeNam || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        📄 GSTIN:
                      </span>
                      <p className={`font-mono font-bold ${
                        darkMode ? 'text-blue-400' : 'text-gray-900'
                      }`}>
                        {verificationResult.gstin || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🆔 PAN:
                      </span>
                      <p className={`font-mono font-bold ${
                        darkMode ? 'text-blue-400' : 'text-gray-900'
                      }`}>
                        {verificationResult.extractedData?.panNo || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        ⚖️ Constitution:
                      </span>
                      <p className={`font-medium ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {verificationResult.ctb || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        📅 Registration Date:
                      </span>
                      <p className={`font-medium ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {verificationResult.rgdt || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        📊 Status:
                      </span>
                      <p className={`font-bold ${verificationResult.sts === 'Active' ? 'text-green-500' : 'text-red-500'}`}>
                        {verificationResult.sts || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Taxpayer Type:</span>
                      <p className="text-gray-900">{verificationResult.dty || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {verificationResult.pradr?.adr && (
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🏠 Registered Address:
                      </span>
                      <p className={`text-sm leading-relaxed mt-1 p-3 rounded-xl border-2 ${
                        darkMode 
                          ? 'text-white bg-slate-800 border-slate-600' 
                          : 'text-gray-900 bg-purple-50 border-purple-200'
                      }`}>
                        {verificationResult.pradr.adr}
                      </p>
                    </div>
                  )}
                  
                  {verificationResult.nba && verificationResult.nba.length > 0 && (
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        💼 Nature of Business:
                      </span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {verificationResult.nba.map((business, index) => (
                          <span 
                            key={index}
                            className={`px-3 py-1.5 text-xs font-bold rounded-full ${
                              darkMode
                                ? 'bg-blue-600/30 text-blue-300 border border-blue-500'
                                : 'bg-blue-100 text-blue-800 border border-blue-200'
                            }`}
                          >
                            {business}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {verificationResult.stj && (
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🏛️ State Jurisdiction:
                      </span>
                      <p className={`font-medium text-sm mt-1 ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {verificationResult.stj}
                      </p>
                    </div>
                  )}

                  {verificationResult.ctj && (
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🏢 Center Jurisdiction:
                      </span>
                      <p className={`font-medium text-sm mt-1 ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {verificationResult.ctj}
                      </p>
                    </div>
                  )}

                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t-2 ${
                    darkMode ? 'border-slate-700' : 'border-purple-200'
                  }`}>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🆔 Aadhaar Verified:
                      </span>
                      <p className={`font-bold ${verificationResult.adhrVFlag === 'Yes' ? 'text-green-500' : 'text-orange-500'}`}>
                        {verificationResult.adhrVFlag || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        📧 E-Invoice Status:
                      </span>
                      <p className={`font-bold ${verificationResult.einvoiceStatus === 'Yes' ? 'text-green-500' : 'text-gray-500'}`}>
                        {verificationResult.einvoiceStatus || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className={`font-bold text-xs uppercase tracking-wider ${
                        darkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        🔍 Field Visit:
                      </span>
                      <p className={`font-bold ${verificationResult.isFieldVisitConducted === 'Yes' ? 'text-green-500' : 'text-gray-500'}`}>
                        {verificationResult.isFieldVisitConducted || 'N/A'}
                      </p>
                    </div>
                    {verificationResult.adhrVdt && (
                      <div>
                        <span className={`font-bold text-xs uppercase tracking-wider ${
                          darkMode ? 'text-green-300' : 'text-green-700'
                        }`}>
                          📅 Aadhaar Verification Date:
                        </span>
                        <p className={`font-medium ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {verificationResult.adhrVdt}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t-2 ${
              darkMode ? 'border-slate-700' : 'border-purple-200'
            }`}>
              <button
                type="button"
                onClick={onClose}
                disabled={isVerifying}
                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-md min-h-[44px] touch-manipulation ${
                  isVerifying
                    ? 'opacity-50 cursor-not-allowed'
                    : darkMode
                      ? 'text-slate-300 bg-slate-700 border-2 border-slate-600 hover:bg-slate-600 hover:border-slate-500'
                      : 'text-gray-700 bg-white border-2 border-purple-300 hover:bg-purple-50 hover:border-purple-400'
                }`}
              >
                ❌ Cancel
              </button>
              
              {verificationResult ? (
                <button
                  type="button"
                  onClick={useVerifiedData}
                  className="px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-md min-h-[44px] touch-manipulation bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
                >
                  ✅ Use This Data
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isVerifying || isLoadingCaptcha || !gstin || !captchaCode}
                  className={`px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 min-h-[44px] touch-manipulation ${
                    isVerifying || isLoadingCaptcha || !gstin || !captchaCode
                      ? 'opacity-50 cursor-not-allowed bg-gray-400 text-gray-200'
                      : darkMode
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                  }`}
                >
                  {isVerifying && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isVerifying ? 'Verifying...' : '🔍 Verify GST'}</span>
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GSTVerificationModal;
